import {
    Alert,
    Button,
    Group,
    Modal,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {notifications} from '@mantine/notifications'
import {IconChevronDown, IconDownload} from '@tabler/icons-react'
import {Link, useNavigate} from '@tanstack/react-router'
import {useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {useCurrentUser} from 'src/auth/use-current-user'
import {CustomerPocSelector} from 'src/customers/customer-poc-selector'
import {useCustomers} from 'src/customers/use-customers'
import {ownsWork, viewOnlyNote} from 'src/jobs/edit-rights'

import {
    createEmptyQuoteDraft,
    draftToCreateInput,
    draftToUpdateInput,
    hasUnsavedChanges,
} from './quote-adapters'
import type {QuoteDraft} from './quote-builder-logic'
import {quoteEmailBlockedReason} from './quote-email'
import {QuoteEmailButton} from './quote-email-button'
import {QuoteLineItems} from './quote-line-items'
import {QuoteRentalPeriodField} from './quote-rental-period-field'
import {QuoteStatusBadge, quoteStatusColor, quoteStatusLabel} from './quote-status-badge'
import {getQuoteStatusModalContent} from './quote-status-utils'
import {QuoteTermsField} from './quote-terms-field'
import type {QuoteStatus} from './schemas'
import {useCreateQuote, useDownloadQuotePdf, useQuote, useUpdateQuote} from './use-quotes'
import {useRentalPeriodLines} from './use-rental-period-lines'

const LINE_ITEM_LOCKED_STATUSES = new Set<QuoteStatus>(['accepted', 'rejected', 'voided'])

const QUOTE_DISCLAIMER =
    'This quote is an estimate only. Final billing may vary based on actual field conditions, ' +
    'quantities, and approved change orders. Rates exclude applicable taxes unless noted.'

function parseDateInputValue(value: string | Date | null): Date | null {
    if (value == null) {
        return null
    }

    if (value instanceof Date) {
        return value
    }

    const [year, month, day] = value.split('-').map(Number)
    if (year == null || month == null || day == null) {
        return null
    }

    return new Date(year, month - 1, day)
}

type QuoteBuilderPageProps = {
    mode: 'new' | 'edit'
    quoteId?: string
    projectId?: string | null
    initialDraft?: QuoteDraft
}

export function QuoteBuilderPage({mode, quoteId, projectId, initialDraft}: QuoteBuilderPageProps) {
    const navigate = useNavigate()
    const [quote, setQuote] = useState<QuoteDraft>(() => initialDraft ?? createEmptyQuoteDraft())
    const {data: customers = [], isLoading: isLoadingCustomers} = useCustomers()
    const createQuoteMutation = useCreateQuote()
    const updateQuoteMutation = useUpdateQuote()
    const downloadQuotePdfMutation = useDownloadQuotePdf(quoteId ?? '')
    const [statusModalOpen, setStatusModalOpen] = useState(false)
    const [statusError, setStatusError] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const currentUser = useCurrentUser()
    const {data: savedQuote} = useQuote(quoteId ?? '', {
        enabled: mode === 'edit' && quoteId != null,
    })
    const viewOnly =
        mode === 'edit' &&
        savedQuote != null &&
        !ownsWork(currentUser, {createdById: savedQuote.createdById})
    const emailBlockedReason =
        savedQuote == null
            ? 'Loading the quote…'
            : quoteEmailBlockedReason(savedQuote, {
                  viewOnly,
                  draftContactId: quote.meta.customerContactId,
                  unsavedChanges: hasUnsavedChanges(quote, savedQuote),
              })
    const {updateRentalPeriod, updateLines} = useRentalPeriodLines(setQuote)

    const customerOptions = useMemo(
        () => customers.map((customer) => ({value: customer.id, label: customer.name})),
        [customers],
    )

    const canSave = quote.meta.customerId != null
    const isSaving = createQuoteMutation.isPending || updateQuoteMutation.isPending
    const lineItemsLocked = LINE_ITEM_LOCKED_STATUSES.has(quote.status)
    const detailsLocked = mode === 'edit' && quote.status === 'accepted'

    function updateMeta(patch: Partial<QuoteDraft['meta']>) {
        setQuote((current) => ({
            ...current,
            meta: {...current.meta, ...patch},
        }))
    }

    async function handleSave() {
        if (!canSave) {
            return
        }

        setSaveError(null)
        try {
            if (mode === 'new') {
                const created = await createQuoteMutation.mutateAsync(draftToCreateInput(quote))
                void navigate({
                    to: '/quotes/$quoteId',
                    params: {quoteId: created.id},
                    replace: true,
                })
                return
            }

            if (quoteId == null) {
                return
            }

            await updateQuoteMutation.mutateAsync({
                id: quoteId,
                input: draftToUpdateInput(quote),
            })
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save quote')
        }
    }

    async function handleStatusChange(status: QuoteStatus) {
        if (mode !== 'edit' || quoteId == null || status === quote.status) {
            return
        }

        setStatusError(null)
        try {
            const updated = await updateQuoteMutation.mutateAsync({
                id: quoteId,
                input: {status},
            })
            setQuote((current) => ({
                ...current,
                status: updated.status,
            }))
            setStatusModalOpen(false)
        } catch (error) {
            setStatusError(error instanceof Error ? error.message : 'Failed to update quote status')
        }
    }

    async function handleDownload() {
        if (quoteId == null) {
            return
        }

        try {
            await downloadQuotePdfMutation.mutateAsync()
        } catch (error) {
            notifications.show({
                title: 'Could not download quote PDF',
                message: error instanceof Error ? error.message : 'Something went wrong.',
                color: 'red',
            })
        }
    }

    const statusModalContent = getQuoteStatusModalContent(quote.status)
    const showCreateProject =
        mode === 'edit' &&
        quoteId != null &&
        quote.status === 'accepted' &&
        projectId == null &&
        !viewOnly
    const showGoToProject =
        mode === 'edit' && quoteId != null && quote.status === 'accepted' && projectId != null

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                    <Title order={1}>Quote</Title>
                    <Text size="sm" c="dimmed">
                        {mode === 'new'
                            ? 'Draft quote — changes are not saved yet'
                            : detailsLocked
                              ? 'Accepted quote — changes are locked'
                              : 'Saved quote — use Save to persist edits'}
                    </Text>
                    {savedQuote?.sentAt ? (
                        <Text size="sm" c="dimmed" data-testid="quote-sent-note">
                            Emailed to the customer{' '}
                            {formatBusinessTimestamp(savedQuote.sentAt, 'MMM d, yyyy h:mm a')}
                            {savedQuote.sentByName ? ` by ${savedQuote.sentByName}` : ''}
                        </Text>
                    ) : null}
                </Stack>
                <Group gap="sm" align="center">
                    {mode === 'edit' ? (
                        <>
                            <Button
                                variant="light"
                                leftSection={<IconDownload size={16} stroke={1.5} />}
                                loading={downloadQuotePdfMutation.isPending}
                                onClick={() => void handleDownload()}
                            >
                                Download PDF
                            </Button>
                            {quoteId != null ? (
                                <QuoteEmailButton
                                    quoteId={quoteId}
                                    savedQuote={savedQuote}
                                    blockedReason={emailBlockedReason}
                                    onEmailed={(status) =>
                                        setQuote((current) => ({...current, status}))
                                    }
                                />
                            ) : null}
                            <Button
                                variant="light"
                                color={quoteStatusColor(quote.status)}
                                size="compact-lg"
                                rightSection={<IconChevronDown size={14} stroke={1.5} />}
                                aria-label={`Quote status: ${quoteStatusLabel(
                                    quote.status,
                                )}. View status details and next steps.`}
                                onClick={() => {
                                    setStatusError(null)
                                    setStatusModalOpen(true)
                                }}
                            >
                                {quoteStatusLabel(quote.status)}
                            </Button>
                        </>
                    ) : null}
                    {showCreateProject ? (
                        <Button
                            component={Link}
                            to="/jobs/create"
                            search={{quote: quoteId, new: 1}}
                            data-testid="quote-create-project"
                        >
                            Create order
                        </Button>
                    ) : showGoToProject ? (
                        <Button component={Link} to="/projects/$projectId" params={{projectId}}>
                            Go to order
                        </Button>
                    ) : (
                        <Tooltip
                            label="Select a customer before saving."
                            disabled={canSave}
                            withArrow
                        >
                            {/* Disabled buttons swallow hover events, so the wrapper owns the tooltip and cursor. */}
                            <span style={{cursor: canSave ? undefined : 'not-allowed'}}>
                                <Button
                                    onClick={() => void handleSave()}
                                    disabled={!canSave || viewOnly}
                                    loading={isSaving}
                                    style={canSave ? undefined : {pointerEvents: 'none'}}
                                    data-testid="quote-save"
                                >
                                    Save
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Group>
            </Group>

            {viewOnly ? (
                <Alert color="gray" variant="light" data-testid="quote-read-only-note">
                    {viewOnlyNote(savedQuote?.createdByName, 'quote')}
                </Alert>
            ) : null}

            {saveError ? (
                <Alert color="red" variant="light" data-testid="quote-save-error">
                    {saveError}
                </Alert>
            ) : null}

            <fieldset
                disabled={viewOnly || detailsLocked}
                style={{border: 0, padding: 0, margin: 0, minWidth: 0}}
            >
                <Stack gap="lg">
                    <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                        <Select
                            size="sm"
                            label="Customer"
                            placeholder={
                                isLoadingCustomers ? 'Loading customers…' : 'Select customer'
                            }
                            searchable
                            data={customerOptions}
                            value={quote.meta.customerId}
                            onChange={(value) =>
                                updateMeta({customerId: value, customerContactId: null})
                            }
                            disabled={isLoadingCustomers}
                            nothingFoundMessage="No customers found"
                            required
                        />
                        <CustomerPocSelector
                            customerId={quote.meta.customerId}
                            value={quote.meta.customerContactId}
                            onChange={(customerContactId) => updateMeta({customerContactId})}
                            disabled={quote.meta.customerId == null}
                        />
                        <TextInput
                            size="sm"
                            label="Quote name"
                            placeholder="Optional internal name"
                            value={quote.meta.name}
                            onChange={(event) => updateMeta({name: event.currentTarget.value})}
                        />
                        <TextInput
                            size="sm"
                            label="PO number"
                            value={quote.meta.poNumber}
                            onChange={(event) => updateMeta({poNumber: event.currentTarget.value})}
                        />

                        <QuoteTermsField
                            customerId={quote.meta.customerId}
                            value={quote.meta.terms}
                            isNewQuote={mode === 'new'}
                            onChange={(terms) => updateMeta({terms})}
                        />

                        <DateInput
                            size="sm"
                            label="Due date"
                            value={quote.meta.dueDate}
                            onChange={(value) => updateMeta({dueDate: parseDateInputValue(value)})}
                            clearable
                        />
                    </SimpleGrid>

                    <QuoteRentalPeriodField
                        value={quote.meta.rentalPeriod}
                        onChange={updateRentalPeriod}
                        readOnly={mode === 'edit' && lineItemsLocked}
                    />

                    <Text size="sm" c="dimmed">
                        {QUOTE_DISCLAIMER}
                    </Text>

                    <QuoteLineItems
                        lines={quote.lines}
                        onLinesChange={updateLines}
                        readOnly={mode === 'edit' && lineItemsLocked}
                        rentalPeriod={quote.meta.rentalPeriod}
                    />
                </Stack>
            </fieldset>

            <Modal
                opened={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                title={
                    <Group gap="xs" wrap="nowrap">
                        <Text span>Quote status:</Text>
                        <QuoteStatusBadge status={quote.status} />
                    </Group>
                }
                centered
            >
                <Stack gap="md">
                    <Stack gap={4}>
                        <Text size="sm" fw={600}>
                            Current
                        </Text>
                        <Text size="sm">{statusModalContent.currentDescription}</Text>
                    </Stack>
                    {statusModalContent.nextDescription ? (
                        <Stack gap={4}>
                            <Text size="sm" fw={600}>
                                Next step
                            </Text>
                            <Text size="sm">{statusModalContent.nextDescription}</Text>
                        </Stack>
                    ) : null}
                    {statusError ? (
                        <Text size="sm" c="red">
                            {statusError}
                        </Text>
                    ) : null}
                    {statusModalContent.actions.length > 0 && !viewOnly ? (
                        <Group justify="space-between" align="center">
                            <Group gap="sm">
                                {statusModalContent.actions
                                    .filter((action) => action.destructive)
                                    .map((action) => (
                                        <Button
                                            key={action.targetStatus}
                                            variant="filled"
                                            color="red"
                                            loading={isSaving}
                                            onClick={() =>
                                                void handleStatusChange(action.targetStatus)
                                            }
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                            </Group>
                            <Group gap="sm">
                                {statusModalContent.actions
                                    .filter((action) => !action.destructive)
                                    .map((action) => (
                                        <Button
                                            key={action.targetStatus}
                                            variant={action.variant ?? 'filled'}
                                            loading={isSaving}
                                            onClick={() =>
                                                void handleStatusChange(action.targetStatus)
                                            }
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                            </Group>
                        </Group>
                    ) : null}
                </Stack>
            </Modal>
        </Stack>
    )
}
